import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { getArmexStations, exportPricesToExcel } from '../api_controll/apiAdmin';
import '../styles/ExportPricesModal.css';

const EXPORT_TYPES = [
  {
    id: 'armex-single',
    label: 'Armex CSPH ceny – konkrétní datum',
    includeCompetitors: false,
    range: false,
  },
  {
    id: 'armex-range',
    label: 'Armex CSPH ceny – rozmezí dat',
    includeCompetitors: false,
    range: true,
  },
  {
    id: 'armex-comp-single',
    label: 'Armex + konkurenční CSPH ceny – konkrétní datum',
    includeCompetitors: true,
    range: false,
  },
  {
    id: 'armex-comp-range',
    label: 'Armex + konkurenční CSPH ceny – rozmezí dat',
    includeCompetitors: true,
    range: true,
  },
];

function getStationId(station) {
  return station.id ?? station.station_id ?? station.stationId;
}

export default function ExportPricesModal({ onClose }) {
  const [stations, setStations] = useState([]);
  const [selectedStationIds, setSelectedStationIds] = useState([]);
  const [stationSearch, setStationSearch] = useState('');
  const [exportType, setExportType] = useState(EXPORT_TYPES[0].id);
  const [date, setDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [errors, setErrors] = useState({});
  const today = new Date().toISOString().slice(0, 10);

  const loadStations = useCallback(async () => {
    try {
      setFetchError(null);
      const data = await getArmexStations();
      setStations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setFetchError('Nepodařilo se načíst seznam aktivních Armex CSPH stanic.');
    }
  }, []);

  useEffect(() => {
    loadStations();
  }, [loadStations]);

  const activeStations = useMemo(() => {
    return stations.filter((station) => {
      if ('is_active' in station) {
        return station.is_active === 1 || station.is_active === true || station.is_active === '1';
      }
      if ('active' in station) {
        return station.active === 1 || station.active === true || station.active === '1';
      }
      return true;
    });
  }, [stations]);

  const filteredStations = useMemo(() => {
    const query = stationSearch.trim().toLowerCase();
    if (!query) return activeStations;

    return activeStations.filter((station) => {
      const label = String(station.name || station.station_name || station.id || station.station_id || '');
      return label.toLowerCase().includes(query);
    });
  }, [activeStations, stationSearch]);

  const selectedAll =
    filteredStations.length > 0 &&
    filteredStations.every((station) => selectedStationIds.includes(getStationId(station)));

  const toggleStation = (stationId) => {
    setSelectedStationIds((prev) =>
      prev.includes(stationId) ? prev.filter((id) => id !== stationId) : [...prev, stationId]
    );
    setErrors((prev) => {
      const next = { ...prev };
      delete next.station;
      return next;
    });
  };

  const handleSelectAll = () => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next.station;
      return next;
    });

    if (selectedAll) {
      setSelectedStationIds((prev) => prev.filter((id) => !filteredStations.some((station) => getStationId(station) === id)));
      return;
    }

    setSelectedStationIds((prev) => [
      ...new Set([...prev, ...filteredStations.map((station) => getStationId(station))]),
    ]);
  };

  const clearDateErrors = () => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next.date;
      delete next.dateFrom;
      delete next.dateTo;
      delete next.dateRange;
      return next;
    });
  };

  const currentExport = EXPORT_TYPES.find((item) => item.id === exportType) || EXPORT_TYPES[0];

  const handleExport = async () => {
  const nextErrors = {};

  if (!selectedStationIds.length) {
    nextErrors.station = 'Vyber alespoň jednu Armex CSPH stanici.';
  }

  if (currentExport.range) {
    if (!dateFrom) {
      nextErrors.dateFrom = 'Vyber datum od.';
    } else if (dateFrom > today) {
      nextErrors.dateFrom = 'Datum od nesmí být v budoucnosti.';
    }

    if (!dateTo) {
      nextErrors.dateTo = 'Vyber datum do.';
    } else if (dateTo > today) {
      nextErrors.dateTo = 'Datum do nesmí být v budoucnosti.';
    }

    if (dateFrom && dateTo && dateFrom > dateTo) {
      nextErrors.dateRange = 'Datum od nesmí být později než datum do.';
    }
  } else if (!date) {
    nextErrors.date = 'Vyber datum pro export.';
  } else if (date > today) {
    nextErrors.date = 'Datum nesmí být v budoucnosti.';
  }

  if (Object.keys(nextErrors).length > 0) {
    setErrors(nextErrors);
    return;
  }

  setErrors({});

  const payload = {
    station_ids: selectedStationIds,
    include_competitors: currentExport.includeCompetitors,
    date: currentExport.range ? null : date,
    date_from: currentExport.range ? dateFrom : null,
    date_to: currentExport.range ? dateTo : null,
    output_format: 'xlsx',
  };

  try {
    setLoading(true);

    await exportPricesToExcel(payload);
    toast.success('Export byl stažen do Excelu.');
    onClose?.();
  } catch (err) {
    console.error('EXPORT ERROR:', err);
    toast.error(err?.message || 'Nepodařilo se vytvořit export do Excelu.');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content export-card export-prices-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <button className="modal-close" type="button" onClick={onClose} aria-label="Zavřít modal">
          ×
        </button>

        <div className="export-header" style={{ marginBottom: '1rem' }}>
          <div>
            <p className="export-eyebrow">Export cen</p>
            <h2 className="export-title" style={{ margin: 0, fontSize: '1.25rem' }}>
              Armex CSPH do Excelu
            </h2>
          </div>
        </div>

        <div className="export-grid">
          <div className="export-group">
            <label className="export-label" htmlFor="export-type">
              Exportní scénář
            </label>
            <select
              id="export-type"
              className="export-select"
              value={exportType}
              onChange={(e) => {
                setExportType(e.target.value);
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.date;
                  delete next.dateFrom;
                  delete next.dateTo;
                  delete next.dateRange;
                  return next;
                });
              }}
            >
              {EXPORT_TYPES.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {currentExport.range ? (
            <>
              <div className="export-group">
                <label className="export-label" htmlFor="date-from">
                  Datum od
                </label>
                <input
                  id="date-from"
                  type="date"
                  className="export-input"
                  value={dateFrom}
                  max={today}
                  aria-invalid={!!errors.dateFrom || !!errors.dateRange}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.dateFrom;
                      delete next.dateRange;
                      return next;
                    });
                  }}
                />
                {errors.dateFrom ? <p className="export-error">{errors.dateFrom}</p> : null}
              </div>
              <div className="export-group">
                <label className="export-label" htmlFor="date-to">
                  Datum do
                </label>
                <input
                  id="date-to"
                  type="date"
                  className="export-input"
                  value={dateTo}
                  max={today}
                  aria-invalid={!!errors.dateTo || !!errors.dateRange}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.dateTo;
                      delete next.dateRange;
                      return next;
                    });
                  }}
                />
                {errors.dateTo ? <p className="export-error">{errors.dateTo}</p> : null}
              </div>
              {errors.dateRange ? <div className="export-error" style={{ gridColumn: '1 / -1' }}>{errors.dateRange}</div> : null}
            </>
          ) : (
            <div className="export-group export-group--full">
              <label className="export-label" htmlFor="single-date">
                Datum
              </label>
              <input
                id="single-date"
                type="date"
                className="export-input"
                value={date}
                max={today}
                aria-invalid={!!errors.date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.date;
                    return next;
                  });
                }}
              />
              {errors.date ? <p className="export-error">{errors.date}</p> : null}
            </div>
          )}
        </div>

        <div className="export-group export-group--stations">
          <div className="export-group-heading">
            <div>
              <p className="export-label">Aktivní Armex CSPH stanice</p>
              <p className="export-hint">Vyber stanice pro export.</p>
            </div>
            <button type="button" className="export-button-secondary" onClick={handleSelectAll}>
              {selectedAll ? 'Odznačit vše' : 'Vybrat vše'}
            </button>
          </div>
          {errors.station ? <p className="export-error">{errors.station}</p> : null}

          <input
            type="text"
            className="export-input"
            placeholder="Hledat stanici..."
            value={stationSearch}
            onChange={(e) => {
              setStationSearch(e.target.value);
              setErrors((prev) => {
                const next = { ...prev };
                delete next.station;
                return next;
              });
            }}
          />

          <div className="export-station-list">
            {fetchError ? (
              <div className="export-empty-state">{fetchError}</div>
            ) : filteredStations.length === 0 ? (
              <div className="export-empty-state">Nenalezena žádná aktivní stanice.</div>
            ) : (
              filteredStations.map((station) => {
                const stationId = getStationId(station);
                return (
                  <label key={stationId} className="export-station-item">
                    <input
                      type="checkbox"
                      checked={selectedStationIds.includes(stationId)}
                      onChange={() => toggleStation(stationId)}
                    />
                    <span>{station.name || station.station_name || `ID ${stationId}`}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="export-actions" style={{ justifyContent: 'space-between' }}>
          <button
            type="button"
            className="export-button-primary"
            onClick={handleExport}
            disabled={loading || !!fetchError}
          >
            {loading ? 'Generuji export…' : 'Exportovat do Excelu'}
          </button>
          <button type="button" className="export-button-secondary" onClick={onClose}>
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}
